import React, { useState, useEffect } from 'react';
import classes from './Result.module.css';
import { getServiceBiomarker, isBiomarkerValueValid } from '../../../utils/miscUtils';
import { fetchSalesOrders, SalesOrderInfo } from '../../../store/slices/salesOrders';
import { DiagnosticResult, fetchDiagnosticResults } from '../../../store/slices/diagnosticResults';
import CustomTextField from '../../../components/UI/Inputs/CustomTextField';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import {
  fetchServicesWithBiomarkerInfo,
  IServiceWithBiomarkerInfo,
  selectServicesWithBiomarkerInfos,
} from '../../../store/slices/services';
import PrimaryButtonV2 from '../../../components/UI/Buttons/PrimaryButtonV2';
import { client } from '../../../networking/client';
import { toast } from 'react-toastify';

import CustomDropdown from '../../../components/UI/Inputs/CustomDropdown';
import {
  IAccountInfo,
  IPerson,
  selectAccountInfo,
  selectPersonLoggedIn,
  selectPersonsInLocation,
} from '../../../store/slices/auth';
import {
  IAttachment,
  IDigitalHealthProfile,
} from '../../../components/UI/SelectedSalesOrder/DigitalHealthProfileTab';
import downloadIcon from '../../../assets/icons/download-icon.svg';
import { JSONModal } from '../Modals/JSONModal';
import Spinner from '../../../components/UI/Spinner/Spinner';
import Modal from '../../../components/UI/Modals/Modal';
import { findProductWithTaIdAndTransactionId } from '../../../helpers';
import { useTranslation } from '../../../hooks/useTranslations';

const Result: React.FC<{
  selectedSalesOrder: SalesOrderInfo;
  setSelectedSalesOrder: (val: SalesOrderInfo) => void;
}> = ({ selectedSalesOrder, setSelectedSalesOrder }) => {
  const dispatch = useAppDispatch();
  const servicesWithBiomarkerInfo = useAppSelector<IServiceWithBiomarkerInfo[]>(
    selectServicesWithBiomarkerInfos
  );
  const { translations } = useTranslation();
  const personsInLocation = useAppSelector<IPerson[]>(selectPersonsInLocation);
  const [personIdSelected, setPersonIdSelected] = useState<number | undefined>(
    selectedSalesOrder.work_order_service_diagnostic?.diagnostic_validation_employee_id ?? undefined
  );
  const [selectedBarcode, setSelectedBarcode] = React.useState<string>();
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const [isFetchingDhp, setIsFetchingDhp] = useState<boolean>(false);
  const [productOrder, setProductOrder] = useState<IDigitalHealthProfile>();
  const [isAddingResults, setIsAddingResults] = useState<boolean>(false);
  const [showConfirmVoidModal, setShowConfirmVoidModal] = useState<boolean>(false);
  const [isResultDeliveryDisabled, setIsResultDeliveryDisabled] = useState<boolean>(false);
  const [isSaveCompleted, setIsSaveCompleted] = useState<boolean>(false);
  const personLoggedIn = useAppSelector<IPerson | undefined>(selectPersonLoggedIn);

  useEffect(() => {
    dispatch(fetchServicesWithBiomarkerInfo());
  }, [dispatch]);

  useEffect(() => {
    const fetchDHP = async () => {
      const productFound = await findProductWithTaIdAndTransactionId(selectedSalesOrder);
      setProductOrder(productFound);
      setIsFetchingDhp(false);
    };
    if (
      selectedSalesOrder.work_order?.work_order_status_id === 2 ||
      selectedSalesOrder.work_order?.work_order_status_id === 3
    ) {
      setIsFetchingDhp(true);
      fetchDHP();
    }
  }, [selectedSalesOrder, accountInfo]);

  const barcodeId = React.useMemo(() => {
    for (const ds of selectedSalesOrder.diagnostic_results ?? []) {
      if (ds.sample_barcode) {
        return ds.sample_barcode;
      }
    }
    return '-1';
  }, []);

  const markAsVoidHandler = async () => {
    if (
      selectedSalesOrder?.diagnostic_results &&
      selectedSalesOrder.diagnostic_results.length > 0
    ) {
      try {
        setIsAddingResults(true);
        await client.put(`/healthpoint/update-status-diagnostic/${accountInfo.account_number}`, {
          merchantPersonId: personLoggedIn?.person_external_id,
          workOrderId: selectedSalesOrder.work_order_service_diagnostic?.work_order_id,
          workOrderStatusId: 3,
          positiveNegative: 'void',
          taId: selectedSalesOrder.sales_order.ta_id,
          transactionId: selectedSalesOrder.sales_order.transaction_id,
          salesOrderId: selectedSalesOrder.sales_order.sales_order_id,
          salesOrderExternalId: selectedSalesOrder.sales_order.sales_order_external_id,
          externalSalesOrderItemId:
            selectedSalesOrder.sales_order_item?.external_sales_order_item_id,
          personEmail: selectedSalesOrder.customer_person.person_email,
          personAccountNumber: selectedSalesOrder.customer_person.person_account_number,
          testId:
            selectedSalesOrder.product_group.product_group_id != 6
              ? selectedSalesOrder.diagnostic_results[0].sample_barcode
              : undefined,
          validatedByPersonId: personIdSelected,
        });
        dispatch(fetchSalesOrders());
        dispatch(fetchDiagnosticResults());
        toast.success(translations('success').toString(), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        setIsResultDeliveryDisabled(true);
        setShowConfirmVoidModal(false);
      } catch (_) {
      } finally {
        setIsAddingResults(false);
        setShowConfirmVoidModal(false);
      }
    }
  };

  const handleSave = async () => {
    if (
      selectedSalesOrder?.diagnostic_results &&
      selectedSalesOrder.diagnostic_results.length > 0
    ) {
      try {
        setIsSaveCompleted(true);
        let valuesLeftToAttach = selectedSalesOrder.diagnostic_results.length;
        let positiveNegative: string | undefined;
        for (const diagnosticResult of selectedSalesOrder?.diagnostic_results) {
          if (diagnosticResult.biomarker_value_result) {
            const serviceWithBiomarker = getServiceBiomarker(
              servicesWithBiomarkerInfo,
              diagnosticResult.biomarker_id,
              diagnosticResult.service_id
            );
            if (
              !isBiomarkerValueValid(
                serviceWithBiomarker?.minValue,
                serviceWithBiomarker?.maxValue,
                diagnosticResult.biomarker_value_result
              )
            ) {
              throw new Error(translations('error.biomarker').toString());
            }
          }
          await client.put(`/healthpoint/diagnostic-result/${diagnosticResult.sample_taken_id}`, {
            biomarkerValueResults: diagnosticResult.biomarker_value_result,
            biomarkerTextResult: diagnosticResult.biomarker_text_result,
            biomarkerId: diagnosticResult.biomarker_id,
          });
          if (diagnosticResult.biomarker_value_result || diagnosticResult.biomarker_text_result) {
            valuesLeftToAttach -= 1;
          }
          if (diagnosticResult.biomarker_text_result) {
            positiveNegative = diagnosticResult.biomarker_text_result;
          }
        }
        toast.success(translations('successfully.updated').toString(), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        dispatch(fetchSalesOrders());
        dispatch(fetchDiagnosticResults());
        if (valuesLeftToAttach === 0) {
          if (selectedSalesOrder.service.service_name.includes('PCR')) {
            await client.put(
              `/healthpoint/update-status-diagnostic/${accountInfo.account_number}`,
              {
                merchantPersonId: personLoggedIn?.person_external_id,
                workOrderId: selectedSalesOrder.work_order_service_diagnostic?.work_order_id,
                workOrderStatusId: positiveNegative?.toLowerCase() === 'void' ? 3 : 2,
                positiveNegative: positiveNegative,
                ctValue:
                  selectedSalesOrder.diagnostic_results[1].biomarker_value_result?.toString(),
                taId: selectedSalesOrder.sales_order.ta_id,
                transactionId: selectedSalesOrder.sales_order.transaction_id,
                salesOrderId: selectedSalesOrder.sales_order.sales_order_id,
                salesOrderExternalId: selectedSalesOrder.sales_order.sales_order_external_id,
                externalSalesOrderItemId:
                  selectedSalesOrder.sales_order_item?.external_sales_order_item_id,
                personEmail: selectedSalesOrder.customer_person.person_email,
                personAccountNumber: selectedSalesOrder.customer_person.person_account_number,
                testId: selectedSalesOrder.diagnostic_results[0].sample_barcode,
                validatedByPersonId: personIdSelected,
              }
            );
          } else {
            await client.put(
              `/healthpoint/update-status-diagnostic/${accountInfo.account_number}`,
              {
                merchantPersonId: personLoggedIn?.person_external_id,
                workOrderId: selectedSalesOrder.work_order_service_diagnostic?.work_order_id,
                workOrderStatusId: 2,
                positiveNegative: positiveNegative,
                details: {
                  diagnose_results: selectedSalesOrder.diagnostic_results
                    .filter((d) => d.biomarker_value_result)
                    .map((diagnosticResult) => {
                      return {
                        property_id: parseInt(diagnosticResult.biomarker_external_id.toString()),
                        property_name: diagnosticResult.biomarker_name,
                        property_value: diagnosticResult.biomarker_value_result?.toString(),
                        machine_id: diagnosticResult.diagnostic_machine_external_id,
                      };
                    }),
                },
                taId: selectedSalesOrder.sales_order.ta_id,
                transactionId: selectedSalesOrder.sales_order.transaction_id,
                salesOrderId: selectedSalesOrder.sales_order.sales_order_id,
                salesOrderExternalId: selectedSalesOrder.sales_order.sales_order_external_id,
                externalSalesOrderItemId:
                  selectedSalesOrder.sales_order_item?.external_sales_order_item_id,
                personEmail: selectedSalesOrder.customer_person.person_email,
                personAccountNumber: selectedSalesOrder.customer_person.person_account_number,
                testId:
                  selectedSalesOrder.product_group.product_group_id != 6
                    ? selectedSalesOrder.diagnostic_results[0].sample_barcode
                    : undefined,
                validatedByPersonId: personIdSelected,
              }
            );
          }
          toast.success(translations('successfully.updated').toString(), {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
          dispatch(fetchSalesOrders());
          dispatch(fetchDiagnosticResults());
          setIsResultDeliveryDisabled(true);
        }
      } catch (err: any) {
        console.log(err);
        toast.error(
          err?.response?.data?.error ?? translations('placeholder.error.result').toString(),
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
        setIsSaveCompleted(false);
      }
    }
  };

  const isVoid = selectedSalesOrder.work_order?.work_order_status_id === 3;
  const handleDownload = async () => {
    const attachmentWithTestResult = productOrder?.attachments?.find(
      (att: IAttachment) => att.attachment_type_id === 1
    );
    if (isVoid) return;
    if (attachmentWithTestResult && attachmentWithTestResult.link) {
      window.open(
        `${window.location.protocol}//${window.location.host}${attachmentWithTestResult.link}`,
        '_blank'
      );
    } else {
      toast.error(translations('error.no_attachments').toString(), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  return (
    <div className={classes.container}>
      <div style={{ display: 'flex', justifyContent: 'end', opacity: isFetchingDhp ? 0.5 : 1 }}>
        {isFetchingDhp && <div>{translations('fetching.data')}</div>}
        {!isFetchingDhp && barcodeId !== '-1' && (
          <div
            className={classes.jsonButton}
            onClick={() => {
              setSelectedBarcode(barcodeId);
            }}
          >
            <div className={classes.downloadText}>{translations('more.details')}</div>
            {productOrder && <div style={{ marginLeft: 15 }}>|</div>}
          </div>
        )}
        {productOrder && !isFetchingDhp && (
          <div
            className={classes.downloadButton}
            style={isVoid ? { opacity: 0.6 } : {}}
            onClick={handleDownload}
          >
            <div className={classes.downloadText}>{translations('download')}</div>
            <img className={classes.downloadIcon} src={downloadIcon} alt="download" />
          </div>
        )}
      </div>
      <div className={classes.diagnosticHolder}>
        <div className={classes.diagnosticLabel}>{translations('diagnostic.type')}</div>
        <div className={classes.diagnostic}>{selectedSalesOrder?.service.service_name}</div>
      </div>
      <div className={classes.diagnosticHolder}>
        <div className={classes.diagnosticLabel}>{translations('select.employee')}</div>
        <CustomDropdown
          placeholder={translations('select.employee.validating')}
          onChange={(e: any) => setPersonIdSelected(e.target.value)}
          options={personsInLocation.map((person) => {
            return {
              id: person.person_id,
              label: `${person.person_firstname} ${person.person_lastname}`,
            };
          })}
          value={personIdSelected}
          disabled={
            selectedSalesOrder.work_order?.work_order_status_id === 2 ||
            selectedSalesOrder.work_order?.work_order_status_id === 3
          }
          maxWidth={'342px'}
        />
      </div>
      <div className={classes.biomarkerTableContainer}>
        <div className={classes.biomarkerTableRow}>
          <div className={classes.biomarkerHeaderItemMedium}>{translations('biomarkers')}</div>
          <div className={classes.biomarkerHeaderItemMedium}>{translations('abbreviation')}</div>
          <div className={classes.biomarkerHeaderItemLarge}>{translations('value')}</div>
          <div className={classes.biomarkerHeaderItemLarge}>{translations('UoM')}</div>
        </div>
        {selectedSalesOrder?.diagnostic_results
          ?.sort((a, b) => {
            if (a.biomarker_order && b.biomarker_order) {
              return a.biomarker_order < b.biomarker_order ? -1 : 1;
            }
            return 1;
          })
          .map((diagnosticResult) => {
            const serviceWithBiomarker = getServiceBiomarker(
              servicesWithBiomarkerInfo,
              diagnosticResult.biomarker_id,
              diagnosticResult.service_id
            );
            return (
              <div key={diagnosticResult.biomarker_id} className={classes.biomarkerTableRow}>
                <div className={classes.biomarkerRowItemMedium}>
                  {diagnosticResult.biomarker_name}
                </div>
                <div className={classes.biomarkerRowItemMedium}>
                  {diagnosticResult.biomarker_short_abbreviation
                    ? `(${diagnosticResult.biomarker_short_abbreviation})`
                    : ''}
                </div>
                {diagnosticResult.biomarker_uom === 'Pos/Neg' ? (
                  <select
                    className={classes.biomarkerRowItemLarge}
                    disabled={
                      selectedSalesOrder.work_order?.work_order_status_id === 2 ||
                      selectedSalesOrder.work_order?.work_order_status_id === 3
                    }
                    value={diagnosticResult.biomarker_text_result ?? ''}
                    onChange={(e: any) => {
                      let tempoSelectedOrder: SalesOrderInfo = { ...selectedSalesOrder };
                      let a: DiagnosticResult[] = [];
                      tempoSelectedOrder.diagnostic_results?.forEach((d) => {
                        if (
                          d.sample_taken_id === diagnosticResult.sample_taken_id &&
                          d.biomarker_id === diagnosticResult.biomarker_id
                        ) {
                          a.push({
                            ...d,
                            biomarker_text_result: e.target.value,
                          });
                        } else {
                          a.push({ ...d });
                        }
                      });
                      tempoSelectedOrder.diagnostic_results = [...a];
                      setSelectedSalesOrder(tempoSelectedOrder);
                    }}
                  >
                    <option disabled value="">
                      {translations('select.result')}
                    </option>
                    <option value="Positive">{translations('positive')}</option>
                    <option value="Negative">{translations('negative')}</option>
                    <option value="Void">{translations('void')}</option>
                  </select>
                ) : (
                  <div className={classes.biomarkerRowItemLarge}>
                    <CustomTextField
                      disabled={
                        isSaveCompleted ||
                        selectedSalesOrder.work_order?.work_order_status_id === 2 ||
                        selectedSalesOrder.work_order?.work_order_status_id === 3 ||
                        (!!selectedSalesOrder.work_order_service_diagnostic?.outside_laboratory &&
                          !selectedSalesOrder.work_order_service_diagnostic?.result_delivery_time)
                      }
                      value={diagnosticResult.biomarker_value_result?.toString() ?? ''}
                      type="number"
                      onChange={(e: any) => {
                        let tempoSelectedOrder: SalesOrderInfo = { ...selectedSalesOrder };
                        let a: DiagnosticResult[] = [];
                        tempoSelectedOrder.diagnostic_results?.forEach((d) => {
                          if (
                            d.sample_taken_id === diagnosticResult.sample_taken_id &&
                            d.biomarker_id === diagnosticResult.biomarker_id
                          ) {
                            a.push({
                              ...d,
                              biomarker_value_result: e.target.value,
                            });
                          } else {
                            a.push({ ...d });
                          }
                        });
                        tempoSelectedOrder.diagnostic_results = [...a];
                        setSelectedSalesOrder(tempoSelectedOrder);
                      }}
                      biomarkerValues={{
                        value1: serviceWithBiomarker?.minValue,
                        value2: serviceWithBiomarker?.maxValue,
                      }}
                      errorValidation={
                        !(
                          serviceWithBiomarker?.minValue &&
                          diagnosticResult?.biomarker_value_result &&
                          serviceWithBiomarker.minValue <=
                            +parseFloat(diagnosticResult.biomarker_value_result.toString()).toFixed(
                              2
                            )
                        ) ||
                        !(
                          serviceWithBiomarker?.maxValue &&
                          diagnosticResult?.biomarker_value_result &&
                          serviceWithBiomarker.maxValue >=
                            +parseFloat(diagnosticResult.biomarker_value_result.toString()).toFixed(
                              2
                            )
                        )
                      }
                      height={'40px'}
                    />
                  </div>
                )}
                <div className={classes.biomarkerRowItemMedium}>
                  {diagnosticResult.biomarker_uom}
                </div>
              </div>
            );
          })}
      </div>
      <div className={classes.buttonHolder}>
        <PrimaryButtonV2
          text={translations('save')}
          containerStyle={{
            width: '100%',
            justifyContent: 'start',
            marginTop: 'auto',
          }}
          buttonStyle={{
            padding: '10px',
            height: 50,
            width: 300,
          }}
          disabled={
            !personIdSelected ||
            selectedSalesOrder.work_order?.work_order_status_id === 2 ||
            selectedSalesOrder.work_order?.work_order_status_id === 3 ||
            isResultDeliveryDisabled ||
            isSaveCompleted
          }
          onClick={() => handleSave()}
        />
        {!selectedSalesOrder?.service.service_name?.toLowerCase().includes('pcr') && (
          <PrimaryButtonV2
            text={translations('void')}
            containerStyle={{
              width: '100%',
              justifyContent: 'start',
              marginTop: '20px',
            }}
            buttonStyle={{
              height: 46,
              width: 300,
              borderRadius: 8,
            }}
            onClick={() => setShowConfirmVoidModal(true)}
            invertedColors={true}
            disabled={
              !personIdSelected ||
              selectedSalesOrder.work_order?.work_order_status_id === 2 ||
              selectedSalesOrder.work_order?.work_order_status_id === 3 ||
              isResultDeliveryDisabled
            }
          />
        )}
      </div>
      <Modal
        isOpen={showConfirmVoidModal}
        onClose={() => setShowConfirmVoidModal(false)}
        containerPadding="20px 20px 20px 20px"
        width={'50%'}
        maxWidth="900px"
        header={translations('are.you.sure')}
      >
        {isAddingResults ? (
          <Spinner />
        ) : (
          <div className={classes.voidButtons}>
            <PrimaryButtonV2
              text={translations('yes')}
              containerStyle={{
                width: '150px',
                marginTop: 20,
                marginRight: 30,
              }}
              buttonStyle={{
                height: 46,
                width: '100%',
                borderRadius: 8,
              }}
              onClick={markAsVoidHandler}
            />
            <PrimaryButtonV2
              text={translations('no')}
              containerStyle={{
                width: '150px',
                marginTop: 20,
              }}
              buttonStyle={{
                height: 46,
                width: '100%',
                borderRadius: 8,
              }}
              onClick={() => setShowConfirmVoidModal(false)}
              invertedColors={true}
            />
          </div>
        )}
      </Modal>
      {selectedBarcode && (
        <JSONModal barcode={selectedBarcode} onClose={() => setSelectedBarcode(undefined)} />
      )}
    </div>
  );
};

export { Result };
